import { useEffect, useState } from "react";
import { FileText, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SearchLoadDropdown } from "./SearchLoadDropdown";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { useAddClaim } from "../hooks/useClaimMutations";
import { useQueryClient } from "@tanstack/react-query";
import { Progress } from "@/components/ui/progress";
import { removeUploadedClaimFiles } from "@/api/receiver/removeUploadedClaimFiles";
import { uploadFile } from "../_utils/uploadFile";

interface AddClaimModalProps {
  loadId?: string;
  loadNameProp?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function AddClaimModal({
  loadId,
  loadNameProp,
  isOpen,
  setIsOpen,
}: AddClaimModalProps) {
  const { mutateAsync: addClaim, isSuccess } = useAddClaim();
  const [load, setLoad] = useState<string | null>(null);
  const [loadName, setLoadName] = useState<string | null>(null);
  const [claimAmount, setClaimAmount] = useState<number | null>(null);
  const [newDetail, setNewDetail] = useState({ message: "" });
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isSaving, setIsSaving] = useState<string | null>(null);
  const [tempId, setTempId] = useState<string | undefined>(undefined);
  const [uploadStatus, setUploadStatus] = useState<Record<string, number>>({});

  const queryClient = useQueryClient();

  // Generate a temporary ID for the claim file upload
  function generateTempId(): string {
    return `temp-${Math.random().toString(36).replace(".", "")}`;
  }

  // Set a new tempId when the modal opens
  useEffect(() => {
    setTempId(generateTempId());
  }, [isOpen]);

  const handleCreateClaim = async () => {
    setIsSaving("Creating claim...");
    if (load === null && loadId === undefined) {
      setError("Please select a load.");
      setIsSaving(null);
      return;
    } else if (!claimAmount || claimAmount <= 0) {
      setError("Please enter a valid claim amount.");
      setIsSaving(null);
      return;
    } else if (newDetail.message.trim() === "") {
      setError("Please enter claim details.");
      setIsSaving(null);
      return;
    }
    setError(null);

    console.log("Creating claim with details:", {
      loadId: load ? load : loadId,
      claimAmount,
      message: newDetail.message,
    });
    setIsSaving("Saving claim...");
    const { id } = await addClaim({
      loadId: (loadId ? loadId : load) as string,
      claimAmount: claimAmount,
      message: newDetail.message,
      tempId: tempId,
    });

    handleClose(true);
  };

  const handleClose = (isSave: boolean = false) => {
    setIsOpen(false);
    setLoad(null);
    setClaimAmount(0);
    setNewDetail({ message: "" });
    setFiles([]);
    setUploadStatus({});
    setDragActive(false);
    setIsSaving(null);
    setError(null);
    if (!isSave && files.length > 0 && tempId) {
      console.log("Removing uploaded files for tempId:", tempId);
      removeUploadedClaimFiles({ tempId });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const newFiles = Array.from(e.dataTransfer.files);
      uploadFile({
        newFiles,
        files,
        setFiles,
        uploadStatus,
        setUploadStatus,
        tempId,
      });
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);

      uploadFile({
        newFiles,
        files,
        setFiles,
        uploadStatus,
        setUploadStatus,
        tempId,
      });
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Claim</DialogTitle>
          <DialogDescription>
            Create a claim for a linehaul load with all necessary details.
          </DialogDescription>
          {error && (
            <Card className="mt-4">
              <div className="p-4 bg-red-200 text-red-800 rounded-lg text-left">
                <p className="text-sm">{error}</p>
              </div>
            </Card>
          )}
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="loadId">Select Load</Label>
              <SearchLoadDropdown
                load={loadId ? loadId : load}
                loadName={loadNameProp ? loadNameProp : loadName}
                setLoad={setLoad}
                setLoadName={setLoadName}
                disabled={loadId !== undefined || load !== null}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="claimAmount">Claim Amount ($)</Label>
              <Input
                id="claimAmount"
                type="number"
                step="10"
                value={claimAmount ? claimAmount : ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    setClaimAmount(null);
                  } else {
                    const numValue = Number(value);
                    setClaimAmount(isNaN(numValue) ? null : numValue);
                  }
                }}
                placeholder="0.00"
                required
              />
            </div>
          </div>
          <div className="border-t pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="details">Claim Details</Label>
            </div>
            <Textarea
              id="details"
              value={newDetail.message}
              onChange={(e) => setNewDetail({ message: e.target.value })}
              placeholder="Enter activity note or update..."
              className="min-h-[80px]"
              required
            />
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Supporting Documents</Label>
          <div
            className={`cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragActive
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById("file-upload")?.click()}
          >
            <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <div className="text-sm text-muted-foreground mb-2">
              Click to upload or drag and drop
            </div>
            <div className="text-xs text-muted-foreground">
              PDF, JPG, PNG up to 15MB each
            </div>
            <input
              id="file-upload"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileInput}
              className="hidden"
            />
          </div>
          {files.length > 0 && (
            <div className="space-y-2 mt-4">
              <Label className="text-sm font-medium">Uploaded Files:</Label>
              {files.map((file, index) => (
                <div className="pt-4" key={index}>
                  <div className="flex items-center justify-between p-2 bg-muted rounded-md">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate max-w-[300px]">
                        {file.name.split(".")[0].length > 30
                          ? file.name.slice(0, 30) + "..." + file.name.slice(-3)
                          : file.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    {/* <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(index)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-3 w-3" />
                  </Button> */}
                    <div>
                      <span
                        className={`text-sm text-muted-foreground ${
                          uploadStatus[file.name] === -1 ? "text-red-500" : ""
                        }`}
                      >
                        {uploadStatus[file.name] === -1
                          ? "Error Uploading"
                          : uploadStatus[file.name] === 100
                          ? "Complete"
                          : "Uploading..."}
                      </span>
                    </div>
                  </div>
                  <Progress
                    value={uploadStatus ? uploadStatus[file.name] : 0}
                    className={` h-2 
                    `}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="mt-8">
          <Button variant="outline" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateClaim} disabled={isSaving !== null}>
            {isSaving ? isSaving : "Create Claim"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
